import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { fonts, space } from 'folio-common-components';
import { removeWhitespace } from 'folio-common-utils';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { type RootState, useActions, useSelector } from '../../../state';
import { selectors } from '../../../state/founding';
import { getSuccessfulSignatures } from '../../../state/founding/selectors';
import { track } from '../../../utils/track';
import { SigningProse } from '../../SigningProse';
import { DummyButton, TextButton } from '../../buttons';
import { StepContainer } from '../Container';
import { StepHeading } from '../step-heading';

const BankIdLink = styled.a`
  text-decoration: none;
`;

function selector(state: RootState) {
  const signers = selectors.signings(state);
  const formOwner = signers.find(
    signer => signer.id === state.editor.formOwner,
  );
  const successfulSignatures = getSuccessfulSignatures(state);
  const formOwnerHasSigned = successfulSignatures.includes(
    state.editor.formOwner,
  );

  return {
    id: state.founding.id,
    formOwner,
    formOwnerHasSigned,
    multipleSigners: signers.length > 1,
    signingUrl: formOwner ? formOwner.url : undefined,
    companyName: state.founding.foundingDetails
      ? state.founding.foundingDetails.name
      : '',
    // This should actually be called `formOwner`, but use this
    // to minimize the diff with the main branch
    formOwnerPerson: state.editor.people[state.editor.formOwner],
  };
}

export const SignStep: React.FC = () => {
  const {
    id,
    signingUrl,
    companyName,
    formOwner,
    multipleSigners,
    formOwnerPerson,
  } = useSelector(selector);
  const { foundingThunks } = useActions();
  const { editorThunks } = useActions();
  const { trackFormOwnerInfo } = editorThunks;
  const hasTracked = React.useRef(false);

  React.useEffect(() => {
    // This is normally tracked much earlier, but
    // if that hasn't happened, we do it here
    trackFormOwnerInfo();
  }, [trackFormOwnerInfo]);

  React.useEffect(() => {
    if (hasTracked.current) {
      return;
    }

    window.dataLayer?.push({
      event: 'fullfort-skjema',
      'application.id': id,
      'user.id': formOwnerPerson.id,
      'user.name': formOwnerPerson.name,
      'user.email': formOwnerPerson.email,
      'user.phone': removeWhitespace(formOwnerPerson.phone),
    });

    hasTracked.current = true;
  }, [hasTracked, id, formOwnerPerson]);

  if (signingUrl == null || formOwner == null) {
    return null;
  }

  return (
    <StepContainer>
      <StepHeading>Signer dokumentet</StepHeading>

      {new Date().getFullYear() < 2025 && (
        <InfoWrapper>
          <div css={fonts.font300demi}>Hold an! Det kan lønne seg å vente</div>
          Vent med å signere til 2025, så slipper du å levere regnskap for 2024.
          Med kø hos Brønnøy&shy;sund&shy;registrene og julestri i banken vil du
          uansett ikke rekke å få organisasjons&shy;nummer i 2024. God jul og
          godt nyttår!
        </InfoWrapper>
      )}

      <>
        <SigningProse />

        {multipleSigners ? (
          <p>
            Den som skal signere nå er <strong>{formOwner.name}</strong>. Senere
            skal resten av eierne signere.
          </p>
        ) : null}

        <p>
          <BankIdLink href={signingUrl} target="_blank">
            <DummyButton
              size="large"
              fullWidth={true}
              onClick={() =>
                track(
                  'bankid',
                  'click',
                  `signer med bankid - ${companyName}`,
                  50,
                  false,
                )
              }
            >
              Signer med BankID
            </DummyButton>
          </BankIdLink>
        </p>

        <div
          css={css`
            text-align: center;
          `}
        >
          <TextButton onClick={foundingThunks.reopenThunk}>
            Gå tilbake og gjør endringer
          </TextButton>
        </div>
      </>
    </StepContainer>
  );
};

const InfoWrapper = styled.div`
  ${space([16], 'padding')};
  background-color: ${colors.yellowLight};
  border-radius: var(--border-radius);
`;
